import {
  MeetingFilterRequest,
  MeetingListScope,
} from '@interfaces/meetings/requests/meeting.request';
import { MeetingSource, MeetingStatus } from '@interfaces/meetings/responses/meeting.response';
import { MEETING_CALL_TYPE_LABELS } from '@features/dashboard/meetings/components/meeting-presenter';
import { CLIENT_VISIBILITY_FILTER_VALUES } from '@features/dashboard/meetings/components/meeting-visibility-presenter';

/**
 * Filtros da tela Ligações ↔ querystring (cards 3112 e 3139). Funções puras, com spec isolado.
 *
 * Listas viajam separadas por vírgula (`companyIds=a,b`): ids e códigos de situação/origem não têm
 * vírgula, e a URL fica curta e legível. Os campos únicos antigos (`companyId`, `status`...) ainda
 * são lidos e entram na lista, para os links antigos (e o do deal, `?dealId=`) continuarem valendo.
 */

/** Chaves de lista na URL, na mesma forma do `MeetingFilterRequest`. */
export const CHAVES_DE_LISTA = [
  'companyIds',
  'partnerIds',
  'financialInstitutionIds',
  'dealIds',
  'statuses',
  'sources',
  'callTypes',
  'organizerUserIds',
  'participantEmails',
  // Filtro "Para o cliente" (card 3803).
  'clientVisibility',
] as const;

/** Campo único antigo → lista em que ele entra. */
const CHAVES_ANTIGAS: Record<string, (typeof CHAVES_DE_LISTA)[number]> = {
  companyId: 'companyIds',
  partnerId: 'partnerIds',
  financialInstitutionId: 'financialInstitutionIds',
  dealId: 'dealIds',
  status: 'statuses',
};

const SITUACOES: MeetingStatus[] = [
  'Scheduled',
  'Joining',
  'InWaitingRoom',
  'Recording',
  'Processing',
  'Completed',
  'NoRecording',
  'Failed',
  'Cancelled',
  'BotCancelled',
];
const ORIGENS: MeetingSource[] = ['Calendar', 'Manual'];

type Params = Record<string, unknown>;

function texto(params: Params, chave: string): string | null {
  const valor = params[chave];
  return typeof valor === 'string' && valor.trim() ? valor.trim() : null;
}

function simNao(valor: string | null): boolean | null {
  return valor === 'true' ? true : valor === 'false' ? false : null;
}

/** Lê os filtros da URL. Valor inválido (situação que não existe, lixo) é ignorado. */
export function filtrosDaUrl(params: Params): MeetingFilterRequest {
  const filtros: MeetingFilterRequest = {};

  const de = texto(params, 'from');
  const ate = texto(params, 'to');
  if (de) filtros.from = de;
  if (ate) filtros.to = ate;

  const listas = new Map<string, Set<string>>();
  const juntar = (chave: string, valores: string[]): void => {
    const atual = listas.get(chave) ?? new Set<string>();
    valores.forEach(v => atual.add(v));
    listas.set(chave, atual);
  };

  for (const chave of CHAVES_DE_LISTA) {
    const valor = texto(params, chave);
    if (valor)
      juntar(
        chave,
        valor
          .split(',')
          .map(v => v.trim())
          .filter(Boolean)
      );
  }
  for (const [antiga, lista] of Object.entries(CHAVES_ANTIGAS)) {
    const valor = texto(params, antiga);
    if (valor) juntar(lista, [valor]);
  }

  for (const [chave, valores] of listas) {
    let itens = [...valores];
    if (chave === 'statuses') itens = itens.filter(v => SITUACOES.includes(v as MeetingStatus));
    if (chave === 'sources') itens = itens.filter(v => ORIGENS.includes(v as MeetingSource));
    if (chave === 'callTypes') itens = itens.filter(v => v in MEETING_CALL_TYPE_LABELS);
    if (chave === 'clientVisibility')
      itens = itens.filter(v => (CLIENT_VISIBILITY_FILTER_VALUES as readonly string[]).includes(v));
    if (itens.length) (filtros as Record<string, unknown>)[chave] = itens;
  }

  const comGravacao = simNao(texto(params, 'hasRecording'));
  const comTranscricao = simNao(texto(params, 'hasTranscript'));
  if (comGravacao !== null) filtros.hasRecording = comGravacao;
  if (comTranscricao !== null) filtros.hasTranscript = comTranscricao;
  if (texto(params, 'onlyUnlinked') === 'true') filtros.onlyUnlinked = true;
  if (texto(params, 'participantAttendedOnly') === 'true') filtros.participantAttendedOnly = true;

  return filtros;
}

/**
 * Os filtros como query params (`null` remove a chave). Também zera as chaves antigas: quem entrou
 * por `?companyId=` sai com `?companyIds=`.
 */
export function filtrosParaUrl(filtros: MeetingFilterRequest): Record<string, string | null> {
  const params: Record<string, string | null> = {
    from: filtros.from || null,
    to: filtros.to || null,
    hasRecording:
      filtros.hasRecording === null || filtros.hasRecording === undefined
        ? null
        : String(filtros.hasRecording),
    hasTranscript:
      filtros.hasTranscript === null || filtros.hasTranscript === undefined
        ? null
        : String(filtros.hasTranscript),
    onlyUnlinked: filtros.onlyUnlinked ? 'true' : null,
    participantAttendedOnly: filtros.participantAttendedOnly ? 'true' : null,
  };

  for (const chave of CHAVES_DE_LISTA) {
    const valores = (filtros[chave] as string[] | null | undefined) ?? [];
    params[chave] = valores.length ? valores.join(',') : null;
  }
  for (const antiga of Object.keys(CHAVES_ANTIGAS)) params[antiga] = null;

  return params;
}

// ─────────────────────────── aba da tela (card 3162) ───────────────────────────

/**
 * Abas da tela Meetings, na ordem em que aparecem. A aba Bots agendados saiu (card 3162): os bots
 * agendados são a aba Próximas com o filtro rápido "com bot" (card do topo).
 */
export type AbaDaTela = 'recentes' | 'proximas' | 'todas' | 'arquivadas';
export const ABAS_DA_TELA: readonly AbaDaTela[] = ['recentes', 'proximas', 'todas', 'arquivadas'];

/**
 * Recorte que cada aba pede ao backend (`Scope` de `GET meetings`). **Arquivadas** (card 3786) é o
 * recorte de Todas com `archived=true` (`isArchivedTab`): as outras abas não trazem as arquivadas.
 */
export const ESCOPO_DA_ABA: Record<AbaDaTela, MeetingListScope> = {
  recentes: 'Recent',
  proximas: 'Upcoming',
  todas: 'All',
  arquivadas: 'All',
};

/**
 * A aba que lista só as arquivadas (`archived=true`, card 3786). O valor `'arquivadas'` (que vai para a URL em
 * `?aba=`) segue o padrão dos valores que já existiam (`recentes`, `proximas`, `todas`), para a URL não misturar idiomas.
 */
export function isArchivedTab(tab: AbaDaTela): boolean {
  return tab === 'arquivadas';
}

/** Algum filtro do painel na URL (os campos únicos antigos contam). */
export function temFiltroNaUrl(params: Params): boolean {
  return Object.keys(filtrosDaUrl(params)).length > 0;
}

/**
 * Aba pela URL. Valor válido vale como está; o antigo `aba=agendados` abre Próximas (com o filtro
 * "com bot", ver `comBotDaUrl`). Sem aba: com filtro na URL abre **Todas** — o link antigo (o do deal,
 * `?dealId=`) quer todas as reuniões do recorte, não só as minhas recentes —; sem filtro, **Recentes**.
 * Desde o card 3221 os filtros valem nas três abas, e a URL gravada pela tela já leva a aba.
 */
export function abaDaUrl(params: Params): AbaDaTela {
  const aba = texto(params, 'aba');
  if (aba === 'agendados') return 'proximas';
  if (aba && (ABAS_DA_TELA as readonly string[]).includes(aba)) return aba as AbaDaTela;
  return temFiltroNaUrl(params) ? 'todas' : 'recentes';
}

/**
 * A aba como query param. Recentes é a padrão e não aparece na URL — a não ser com filtro (card
 * 3221): sem aba, link com filtro abre Todas (`abaDaUrl`), e o F5 trocaria Recentes por Todas.
 */
export function abaParaUrl(aba: AbaDaTela, comFiltro = false): string | null {
  return aba === 'recentes' && !comFiltro ? null : aba;
}

/**
 * "Mostrar também as sem gravação" (02/10/2026): por padrão a lista esconde as que já começaram sem gravação
 * (remarcada, cancelada, bot não entrou); `semGravacao=true` mostra todas.
 */
export function mostrarSemGravacaoDaUrl(params: Params): boolean {
  return texto(params, 'semGravacao') === 'true';
}

/**
 * Filtro rápido "só com bot" da aba Próximas (card "Bots agendados" do topo, card 3162):
 * `comBot=true`, ou o link antigo `aba=agendados`.
 */
export function comBotDaUrl(params: Params): boolean {
  return texto(params, 'comBot') === 'true' || texto(params, 'aba') === 'agendados';
}
